import { OrganizationStatus } from "../models/enumerations/organization-status";
import { OrganizationType } from "../models/enumerations/organization-type";
import { PlanType } from "../models/enumerations/plan-type";
import { UserStatus } from "../models/enumerations/user-status";
import { Organization } from "../models/interfaces/organization";
import { OrganizationSubscription } from "../models/interfaces/organization-subscription";
import { User } from "../models/interfaces/user";
import OrganizationService from "./services/organization-service";
import UserService from "./services/user-service";
import { createLogger } from "./logger";

const log = createLogger("OrganizationUtil");

export type AvailableLimits = { userLimit: number; licenseLimit: number };

export default class OrganizationUtil {
    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    /**
     * Determines whether an organization is a sub-organization (i.e. it has a
     * parent organization).
     *
     * @param {Organization | null | undefined} org The organization to check.
     * @returns {boolean} Whether the organization is a sub-organization.
     */
    public static isSubOrganization(org: Organization | null | undefined): boolean {
        return !!org?.parentOrganizationId;
    }

    /**
     * Determines whether a plan supports sub-organizations. Only the
     * Enterprise and Enterprise Trial plans can hold sub-organizations.
     *
     * @param {number | null | undefined} plan The plan value to check.
     * @returns {boolean} Whether the plan supports sub-organizations.
     */
    public static planSupportsSubOrganizations(plan: number | null | undefined): boolean {
        return plan === PlanType.Enterprise || plan === PlanType.EnterpriseTrial;
    }

    /**
     * Determines whether sub-organizations are available for an organization.
     * Sub-organizations are an Enterprise / Enterprise Trial feature; a
     * sub-organization itself carries no plan, so any organization with a
     * parent is supported by construction (its head is Enterprise).
     *
     * @param {Organization | null | undefined} org The organization to check.
     * @returns {boolean} Whether sub-organizations are supported.
     */
    public static supportsSubOrganizations(org: Organization | null | undefined): boolean {
        if (!org) {
            return false;
        }

        if (this.isSubOrganization(org)) {
            return true;
        }

        return this.planSupportsSubOrganizations(org.plan?.value);
    }

    /**
     * Returns the number of sub-organizations held by an organization, from
     * the derived subOrganizationIds cache.
     *
     * @param {Organization | null | undefined} org The organization to check.
     * @returns {number} The number of sub-organizations.
     */
    public static getSubOrganizationCount(org: Organization | null | undefined): number {
        return org?.subOrganizationIds?.length ?? 0;
    }

    /**
     * Determines whether an organization has reached its sub-organization
     * limit. organizationLimit is the number of sub-organizations the
     * organization may hold; the organization itself is not counted toward
     * the limit.
     *
     * @param {Organization | null | undefined} org The organization to check.
     * @returns {boolean} Whether the sub-organization limit has been reached.
     */
    public static isSubOrganizationLimitReached(org: Organization | null | undefined): boolean {
        if (!org) {
            return false;
        }

        return this.getSubOrganizationCount(org) >= Number(org.organizationLimit ?? 1);
    }

    /**
     * Returns an up-to-date count of the users in an organization (optionally
     * including all the sub-organizations of that organization). This is more
     * accurate than the userCount property, which is only updated once a day
     * via firebase function.
     *
     * @param {Organization | null | undefined} org The organization to get user
     * count for. 
     * @param {boolean} includeSubOrgs Whether to include sub organizations in
     * the count (true) or not (false). 
     * @returns {Promise<number>} A promise for the user count.
     */
    public static async getOrganizationUserCount(org: Organization | null | undefined, includeSubOrgs?: boolean): Promise<number> {
        if (!org) {
            return 0;
        }

        let userCount = 0;

        const mainOrgUserCount = await UserService.getCountBy(
            [{
                field: 'organizationId',
                operator: '==',
                value: org.id,
            }, {
                field: "status",
                operator: "!=",
                value: UserStatus.Disabled,
            }],
        );

        userCount += mainOrgUserCount;

        if (org.subOrganizationIds && org.subOrganizationIds.length > 0 && includeSubOrgs) {
            const promises: Promise<number>[] = [];
            org.subOrganizationIds.forEach((o) => {
                promises.push(
                    UserService.getCountBy(
                        [{
                            field: 'organizationId',
                            operator: '==',
                            value: o,
                        }, {
                            field: "status",
                            operator: "!=",
                            value: UserStatus.Disabled,
                        }],
                    )
                        .then((r) => userCount += r)
                )
            });

            await Promise.all(promises);
        }

        return userCount;
    };

    /**
     * Returns how many users can still be created in an organization before
     * reaching its user limit. For sub-organizations the limit is the
     * allocated slice stored on the sub-org; for head organizations it is the
     * remaining capacity stored on the head after carving out sub-org
     * allocations. Returns Infinity when the organization has no user limit.
     *
     * @param {Organization | null | undefined} org The organization to check.
     * @returns {Promise<number>} A promise for the remaining capacity.
     */
    public static async getRemainingUserCapacity(org: Organization | null | undefined): Promise<number> {
        if (!org || org.userLimit == null) {
            return Number.POSITIVE_INFINITY;
        }

        const current = await this.getOrganizationUserCount(org);
        return Math.max(Number(org.userLimit) - current, 0);
    }

    /**
     * Returns the capacity a head organization can still allocate to its
     * sub-organizations: its limit minus its own current usage, for both users
     * and wellness programs. This is the same number the sub-organization
     * form validates against, so tiles and form can never disagree.
     *
     * @param {Organization | null | undefined} org The (head) organization to
     * check.
     * @returns {Promise<{ userLimit: number; licenseLimit: number }>} A
     * promise for the allocatable user and wellness program capacity.
     */
    public static async getAvailableLimits(org: Organization | null | undefined): Promise<AvailableLimits> {
        if (!org) {
            return { userLimit: 0, licenseLimit: 0 };
        }

        const userUsage = await this.getOrganizationUserCount(org);

        return {
            userLimit: Math.max(Number(org.userLimit ?? 0) - userUsage, 0),
            licenseLimit: Math.max(Number(org.mySymmioLicense ?? 0) - Number(org.mySymmioLicenseCount ?? 0), 0),
        };
    }

    /**
     * Returns the allocation summary of an organization's sub-organizations:
     * how many exist and the total user / wellness program capacity allocated
     * to them (the sum of their own allocated slices).
     *
     * @param {Organization | null | undefined} org The (head) organization to
     * check.
     * @returns {Promise<{ subOrganizationCount: number; userLimit: number; licenseLimit: number }>}
     * A promise for the sub-organization allocation summary.
     */
    public static async getSubOrganizationAllocation(org: Organization | null | undefined): Promise<{ subOrganizationCount: number; userLimit: number; licenseLimit: number }> {
        if (!org?.id) {
            return { subOrganizationCount: 0, userLimit: 0, licenseLimit: 0 };
        }

        const subOrgs = await OrganizationService.getBy([{
            field: 'parentOrganizationId',
            operator: '==',
            value: org.id,
        }]);

        return {
            subOrganizationCount: subOrgs.length,
            userLimit: subOrgs.reduce((total, sub) => total + Number(sub.userLimit ?? 0), 0),
            licenseLimit: subOrgs.reduce((total, sub) => total + Number(sub.mySymmioLicense ?? 0), 0),
        };
    }

    /**
     * Updates the subOrganizationId property of a parent organization by
     * querying the firestore for all organizations with a matching
     * parentOrganizationId.
     *
     * @param {string | null | undefined} orgId The id of the organization to be
     * updated .
     * @param {User | null | undefined} user The user performing the update. 
     */
    public static async updateSubOrganizations(orgId: string | null | undefined, user: User | null | undefined) {
        if (!orgId) {
            return;
        }

        const parentOrg = await OrganizationService.get(orgId);

        if (!parentOrg || this.isSubOrganization(parentOrg)) {
            return;
        }

        const subOrgs = await OrganizationService.getBy(
            [{
                field: 'parentOrganizationId',
                operator: '==',
                value: orgId,
            }]
        );

        const subOrgIds: string[] = subOrgs.map((o) => o.id ?? '');


        parentOrg.subOrganizationIds = subOrgIds;

        const updatedOrg = await OrganizationService.save(parentOrg, user);
        return updatedOrg;
    }

    // Converts Business, Core, and Pro plans to deactivated trial on subscription cancellation
    public static convertOrgToTrial(org: Organization, orgSub: OrganizationSubscription): void {
        const previousStatus = org.status;
        const planBefore = org.plan?.value;
        orgSub.accountType = { label: "Trial", value: OrganizationType.Trial };
        orgSub.plan = { label: "Trial", value: PlanType.Trial };
        orgSub.price = 0;
        orgSub.stripePlanSubscription = "";
        orgSub.stripeAddOnSubscription = "";
        orgSub.trialLength = { label: "14 Days", value: 14 };
        org.plan = { label: "Trial", value: PlanType.Trial };
        org.status = OrganizationStatus.Deactivated;

        log.warn("Organization status changed", {
            organizationId: org.id,
            organizationName: org.name,
            fromStatus: OrganizationStatus[Number(previousStatus)] ?? previousStatus,
            fromStatusValue: previousStatus,
            toStatus: OrganizationStatus[Number(org.status)] ?? org.status,
            toStatusValue: org.status,
            planBefore,
            planAfter: PlanType.Trial,
            source: "convertOrgToTrial",
        });
    }


    // #endregion Public Methods
}
